import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { AlertTriangle, ArrowRight, Check, Download, LayoutDashboard, Loader2, Maximize2, MessageSquare, Minimize2, Trash2, X, Zap } from 'lucide-react';
import { Modal, Button, cn } from '@crisellevaldez/speculo-ui';
import ReactMarkdown from 'react-markdown';
import { useAskRemiStore } from '../../stores/useAskRemiStore';
import { useAskRemiEnabled } from '../../stores/useOrganizationStore';
import { usePinnedWidgets } from '../../stores/usePinnedWidgets';
import { useUser } from '../../hooks/useUser';
import { useLiveBlock } from '../../hooks/useLiveBlock';
import { BouncingDots } from '../common/LoadingDots';
import { Typewriter } from '../common/Typewriter';
import type { RemiAsyncJob } from '../../socket/remi';
import type { KpiBlock } from '../../types/remiBlocks';

const quickActions = ['Show my leads', "Today's calls", 'Draft follow-ups', 'Weekly insights'];

const getJobLabel = (job: RemiAsyncJob) =>
  job.jobType === 'remi_query_export' ? 'Background export' : 'Background query';

const getJobPreview = (job: RemiAsyncJob): Record<string, unknown> => {
  const artifactRecord = job.artifact && typeof job.artifact === 'object'
    ? (job.artifact as Record<string, unknown>)
    : undefined;
  const artifactPreview = artifactRecord?.preview_json;
  if (artifactPreview && typeof artifactPreview === 'object') {
    return artifactPreview as Record<string, unknown>;
  }
  return job.result && typeof job.result === 'object' ? job.result : {};
};

const summarizePreviewRows = (preview: Record<string, unknown>): string[] => {
  const rows = Array.isArray(preview.preview_rows) ? preview.preview_rows.slice(0, 2) : [];
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return String(row ?? '');
      return Object.entries(row as Record<string, unknown>)
        .slice(0, 3)
        .map(([key, value]) => `${key}: ${String(value)}`)
        .join(', ');
    })
    .filter(Boolean);
};

const terminalJobStatuses = new Set<RemiAsyncJob['status']>(['completed', 'failed', 'canceled']);

const KpiSuggestionCard: FC<{ block: KpiBlock }> = ({ block }) => {
  const { pinWidget, isPinned } = usePinnedWidgets();
  const [pinning, setPinning] = useState(false);
  const alreadyPinned = isPinned(block);

  const handlePin = async () => {
    setPinning(true);
    await pinWidget(block);
    setPinning(false);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-lg border border-foreground/30 card-surface shadow-sm">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">{block.title}</p>
        <p className="text-2xl font-bold text-primary">
          {typeof block.value === 'number' ? block.value.toLocaleString() : block.value}
          {block.unit && <span className="ml-1 text-sm font-normal text-muted-foreground">{block.unit}</span>}
        </p>
        {block.delta && <p className="text-xs text-green-600">{block.delta}</p>}
      </div>
      <button
        onClick={handlePin}
        disabled={alreadyPinned || pinning}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap shrink-0 w-full sm:w-auto justify-center sm:justify-start',
          alreadyPinned
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'btn-ai-gradient hover:shadow-md'
        )}
      >
        {alreadyPinned ? (
          <>
            <Check className="w-3 h-3" />
            Added
          </>
        ) : pinning ? (
          <>
            <Loader2 className="w-3 h-3 animate-spin" />
            Adding...
          </>
        ) : (
          <>
            <LayoutDashboard className="w-3 h-3" />
            Add to Dashboard
          </>
        )}
      </button>
    </div>
  );
};

const LiveKpiSuggestionCard: FC<{ block: KpiBlock }> = ({ block }) => {
  const liveBlock = useLiveBlock(block);
  return <KpiSuggestionCard block={liveBlock} />;
};

export const AskRemiModal: FC = () => {
  const {
    messages,
    isLoading,
    isModalOpen,
    connectionStatus,
    activeToolUse,
    asyncJobs,
    sendMessage,
    useBackgroundJobInChat,
    dismissAsyncJob,
    closeModal,
    clearMessages,
  } = useAskRemiStore();
  const { enabled: askRemiEnabled } = useAskRemiEnabled();
  const { user } = useUser();
  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'You';

  const [input, setInput] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const hasMessages = messages.length > 0;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isModalOpen]);

  const handleDownloadPdf = useCallback(() => {
    if (!hasMessages) return;

    // Convert markdown text to HTML
    const mdToHtml = (text: string): string => {
      let html = text
        // Escape HTML entities first
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // Bold: **text**
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // Italic: *text*
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Links: [text](url)
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>');

      // Process lines for lists and paragraphs
      const lines = html.split('\n');
      const result: string[] = [];
      let inUl = false;
      let inOl = false;

      for (const line of lines) {
        const trimmed = line.trim();
        const ulMatch = trimmed.match(/^[*\-]\s+(.+)/);
        const olMatch = trimmed.match(/^\d+\.\s+(.+)/);

        if (ulMatch) {
          if (!inUl) { result.push('<ul style="margin:8px 0;padding-left:24px">'); inUl = true; }
          result.push(`<li>${ulMatch[1]}</li>`);
        } else if (olMatch) {
          if (!inOl) { result.push('<ol style="margin:8px 0;padding-left:24px">'); inOl = true; }
          result.push(`<li>${olMatch[1]}</li>`);
        } else {
          if (inUl) { result.push('</ul>'); inUl = false; }
          if (inOl) { result.push('</ol>'); inOl = false; }
          if (trimmed) result.push(`<p style="margin:4px 0">${trimmed}</p>`);
        }
      }
      if (inUl) result.push('</ul>');
      if (inOl) result.push('</ol>');

      return result.join('');
    };

    const date = new Date().toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

    const body = messages
      .filter((m) => !m.isStreaming || m.content)
      .map(
        (m) =>
          `<div style="margin-bottom:20px"><div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:${m.role === 'user' ? '#666' : '#d97706'};margin-bottom:6px">${m.role === 'user' ? userName : 'Remi'}</div><div style="font-size:14px;line-height:1.7">${m.role === 'remi' ? mdToHtml(m.content) : m.content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div></div>`
      )
      .join('<hr style="border:none;border-top:1px solid #eee;margin:16px 0">');

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(
      `<!DOCTYPE html><html><head><title>Ask Remi Conversation</title><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;max-width:700px;margin:40px auto;padding:0 20px;color:#222;line-height:1.6}h1{font-size:18px;border-bottom:2px solid #d97706;padding-bottom:8px;margin-bottom:4px;color:#333}.date{font-size:12px;color:#999;margin-bottom:24px}a{color:#d97706}strong{font-weight:600}ul,ol{margin:6px 0}</style></head><body><h1>Ask Remi — Conversation</h1><p class="date">${date}</p>${body}<p style="margin-top:32px;font-size:11px;color:#999;text-align:center">Exported from Speculo.ai</p></body></html>`
    );
    win.document.close();
    win.print();
  }, [messages, hasMessages]);

  const handleSend = useCallback(
    (text?: string) => {
      const message = text || input.trim();
      if (!message) return;

      setInput('');
      sendMessage(message);
    },
    [input, sendMessage]
  );

  return (
    <Modal isOpen={isModalOpen} onClose={closeModal} size={isFullscreen ? 'full' : 'lg'}>
      <Modal.Header>
        <div className="flex items-center gap-5 w-full">
          <div className="flex items-center gap-2.5">
            <div className="btn-ai-gradient rounded-lg p-1.5">
              <Zap className="h-4 w-4 text-white dark:text-zinc-900" />
            </div>
            <span className="text-base font-semibold text-foreground">Ask Remi</span>
            {connectionStatus === 'connecting' && (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                Connecting
              </span>
            )}
            {connectionStatus === 'connected' && (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
            {connectionStatus === 'error' && (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                Disconnected
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 ml-auto shrink-0">
            {hasMessages && (
              <button
                onClick={handleDownloadPdf}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
            )}
            {hasMessages && (
              <button
                onClick={clearMessages}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => setIsFullscreen((prev) => !prev)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
            >
              {isFullscreen ? (
                <Minimize2 className="w-3.5 h-3.5" />
              ) : (
                <Maximize2 className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div
          className={cn(
            'flex flex-col',
            isFullscreen ? 'h-[calc(100vh-8rem)]' : 'min-h-[400px] max-h-[60vh]'
          )}
        >
          {/* Empty state */}
          {!hasMessages && !isLoading && (
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <p className="text-lg font-semibold text-foreground mb-1 min-h-[1.5em]">
                <Typewriter
                  text="What can I help you with?"
                  startKey={isModalOpen ? 'open' : 'closed'}
                  speed={40}
                  showCursor={false}
                />
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Ask me anything about your leads, calls, or data.
              </p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-md">
                {quickActions.map((label) => (
                  <button
                    key={label}
                    disabled={!askRemiEnabled}
                    onClick={() => askRemiEnabled && handleSend(label)}
                    className={`group p-3 rounded-lg border border-foreground/30 card-surface shadow-sm text-left text-sm font-medium text-foreground leading-snug transition-all duration-200 flex items-center justify-between gap-2 ${askRemiEnabled ? 'hover:border-amber-500/50 hover:shadow-md cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                  >
                    <span>{label}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-foreground opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all duration-200 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation */}
          {(hasMessages || isLoading) && (
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {messages
                .filter((m) => !(m.isStreaming && !m.content))
                .map((msg) => (
                  <div
                    key={msg.id}
                    className={cn('flex gap-3', msg.role === 'user' && 'justify-end')}
                  >
                    {msg.role === 'remi' && (
                      <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shrink-0 mt-0.5">
                        <Zap className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] text-sm leading-relaxed',
                        msg.role === 'user'
                          ? 'bg-muted rounded-2xl px-4 py-2.5 text-foreground'
                          : 'text-foreground pt-0.5'
                      )}
                    >
                      {msg.role === 'remi' ? (
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => (
                              <ul className="list-disc pl-4 mb-2 last:mb-0">{children}</ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="list-decimal pl-4 mb-2 last:mb-0">{children}</ol>
                            ),
                            li: ({ children }) => <li className="mb-0.5">{children}</li>,
                            strong: ({ children }) => (
                              <strong className="font-semibold">{children}</strong>
                            ),
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-600 underline hover:text-amber-700"
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      ) : (
                        msg.content
                      )}
                      {msg.isStreaming && msg.content && (
                        <span className="inline-block w-0.5 h-4 ml-0.5 bg-foreground/60 animate-pulse align-text-bottom" />
                      )}
                      {/* KPI block suggestions */}
                      {msg.kpiBlocks && msg.kpiBlocks.length > 0 && !msg.isStreaming && (
                        <div className="mt-3 space-y-2">
                          {msg.kpiBlocks.map((block) => (
                            <LiveKpiSuggestionCard key={block.id} block={block} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

              {/* Async jobs */}
              {asyncJobs.length > 0 && (
                <div className="space-y-2">
                  {asyncJobs.map((job) => {
                    const preview = getJobPreview(job);
                    const downloadUrl = typeof preview.download_url === 'string' ? preview.download_url : undefined;
                    const filename = typeof preview.filename === 'string' ? preview.filename : 'analytics-export.csv';
                    const rowSummaries = summarizePreviewRows(preview);
                    const canPullIntoChat = terminalJobStatuses.has(job.status);
                    const statusClass = job.status === 'failed'
                      ? 'text-red-600'
                      : job.status === 'completed'
                        ? 'text-green-700'
                        : 'text-muted-foreground';
                    return (
                      <div
                        key={job.jobTaskId}
                        className={cn(
                          'rounded-lg border p-3 text-sm bg-muted/30',
                          job.status === 'failed' ? 'border-red-200 bg-red-50/60 dark:bg-red-950/20' : 'border-border'
                        )}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-foreground">
                              {getJobLabel(job)}
                            </div>
                            <div className={cn('text-xs', statusClass)}>
                              {job.status}{job.message ? `, ${job.message}` : ''}
                            </div>
                          </div>
                          {job.status === 'running' || job.status === 'queued' ? (
                            <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                          ) : job.status === 'completed' ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : job.status === 'failed' ? (
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          ) : null}
                        </div>
                        {job.summary && (
                          <div className="mt-2 text-xs text-muted-foreground line-clamp-3">
                            {job.summary}
                          </div>
                        )}
                        {typeof preview.row_count === 'number' && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            Rows: {preview.row_count.toLocaleString()}
                          </div>
                        )}
                        {rowSummaries.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {rowSummaries.map((row, index) => (
                              <div key={`${job.jobTaskId}-row-${index}`} className="text-xs text-muted-foreground truncate">
                                {row}
                              </div>
                            ))}
                          </div>
                        )}
                        {downloadUrl && (
                          <div className="mt-2">
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={filename}
                              className="inline-flex items-center gap-1.5 text-amber-600 hover:text-amber-700 underline"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download CSV
                            </a>
                          </div>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {canPullIntoChat && (
                            <button
                              type="button"
                              onClick={() => useBackgroundJobInChat(job)}
                              className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background hover:bg-foreground/90"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              Use in chat
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => dismissAsyncJob(job.jobTaskId)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
                          >
                            <X className="w-3.5 h-3.5" />
                            Hide
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tool-use indicator */}
              {activeToolUse && activeToolUse.status === 'starting' && (
                <div className="flex gap-3 items-center">
                  <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                    <Zap className="h-3 w-3 text-white" />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>{activeToolUse.label}</span>
                  </div>
                </div>
              )}

              {/* Loading dots when waiting and no stream yet */}
              {isLoading &&
                !activeToolUse &&
                messages.some((m) => m.isStreaming && !m.content) && (
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                      <Zap className="h-3 w-3 text-white" />
                    </div>
                    <div className="pt-1.5">
                      <BouncingDots />
                    </div>
                  </div>
                )}

              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input bar — hidden when Ask Remi is disabled for the org.
              Summarize still opens this modal and streams a response; users
              just can't type a follow-up reply. */}
          {askRemiEnabled && (
            <div className="pt-3 mt-auto border-t border-border">
              <div className="relative flex items-center gap-3 border border-foreground/30 rounded-lg shadow-sm overflow-hidden transition-all duration-300 focus-within:border-amber-500/80 focus-within:shadow-[0_0_16px_rgba(217,155,20,0.18)] card-surface px-3 py-2.5">
                <div className="btn-ai-gradient rounded-lg p-1.5 shrink-0">
                  <Zap className="h-4 w-4 text-white dark:text-zinc-900" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask Remi anything..."
                  className="flex-1 min-w-0 bg-transparent text-foreground placeholder:text-muted-foreground text-sm outline-none"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleSend()}
                  disabled={!input.trim()}
                  className={cn(
                    'w-8 h-8 rounded-lg transition-all shrink-0',
                    input.trim()
                      ? 'bg-foreground text-background hover:bg-foreground/90'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
